import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ALPHA_PERFORMANCE_SERIES_STROKES,
  type V3TimeFilter,
} from "../../components/performanceChartV3Utils";
import type { FactorEntry } from "../../services/productFactorsService";
import type { StrategyComparisonChartDataResult } from "../../hooks/useStrategyComparisonChartData";
import {
  ALPHA_PERFORMANCE_TIME_FILTER_OPTIONS,
  buildAlphaPerformanceChart,
  buildAlphaPerformanceContext,
} from "./alphaPerformanceMath";

const DEFAULT_SELECTED_PERIOD: V3TimeFilter = "Since inception";
const DEFAULT_CHART_HEIGHT = 560;

export interface AlphaStrategyVsActualComparisonResult
  extends StrategyComparisonChartDataResult {
  strategyFactorsFromSharedStart: FactorEntry[];
  strategyTimeframePerformance: Record<string, number | null>;
  actualTimeframePerformance: Record<string, number | null>;
  actualSummaryFilters: V3TimeFilter[];
}

function getNextClockBoundaryMs(nowMs: number): number {
  const now = new Date(nowMs);
  const nextLocalMidnight = new Date(nowMs);
  nextLocalMidnight.setDate(nextLocalMidnight.getDate() + 1);
  nextLocalMidnight.setHours(0, 0, 0, 0);
  const nextUtcMidnightMs = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() + 1
  );
  return Math.min(nextLocalMidnight.getTime(), nextUtcMidnightMs);
}

function useAlphaPerformanceClock(isActive: boolean, clockKey: string): number {
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!isActive) return undefined;

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const schedule = () => {
      const currentMs = Date.now();
      const delayMs = Math.max(1, getNextClockBoundaryMs(currentMs) - currentMs + 1);
      timeoutId = setTimeout(() => {
        if (cancelled) return;
        setNowMs(Date.now());
        schedule();
      }, delayMs);
    };

    setNowMs(Date.now());
    schedule();

    return () => {
      cancelled = true;
      if (timeoutId !== null) clearTimeout(timeoutId);
    };
  }, [clockKey, isActive]);

  return nowMs;
}

export function useAlphaStrategyVsActualComparison({
  isActive,
  selectedKey,
  strategyFactors,
  actualFactors,
  clockKey = "",
}: {
  isActive: boolean;
  selectedKey: string;
  strategyFactors: FactorEntry[];
  actualFactors: FactorEntry[];
  clockKey?: string;
}): AlphaStrategyVsActualComparisonResult {
  const [selectedPeriod, setSelectedPeriod] = useState<V3TimeFilter>(
    DEFAULT_SELECTED_PERIOD
  );
  const previousIsActiveRef = useRef(isActive);
  const nowMs = useAlphaPerformanceClock(isActive, clockKey);

  useEffect(() => {
    const becameActive = isActive && !previousIsActiveRef.current;
    previousIsActiveRef.current = isActive;
    if (becameActive) setSelectedPeriod(DEFAULT_SELECTED_PERIOD);
  }, [isActive]);

  const context = useMemo(
    () => buildAlphaPerformanceContext({ strategyFactors, actualFactors, nowMs }),
    [actualFactors, nowMs, strategyFactors]
  );

  useEffect(() => {
    if (context.availableFilters.includes(selectedPeriod)) return;
    if (selectedPeriod !== DEFAULT_SELECTED_PERIOD) {
      setSelectedPeriod(DEFAULT_SELECTED_PERIOD);
    }
  }, [context.availableFilters, selectedPeriod]);

  const handleSelectPeriod = useCallback((value: V3TimeFilter) => {
    setSelectedPeriod(value);
  }, []);

  const { chartData, lines } = useMemo(
    () => buildAlphaPerformanceChart({
      context,
      period: selectedPeriod,
      selectedKey,
      strategyColor: ALPHA_PERFORMANCE_SERIES_STROKES.strategy,
      actualColor: ALPHA_PERFORMANCE_SERIES_STROKES.actual,
    }),
    [context, selectedKey, selectedPeriod]
  );

  return {
    loading: false,
    error: null,
    chartData,
    lines,
    chartHeight: DEFAULT_CHART_HEIGHT,
    timeFilterOptions: ALPHA_PERFORMANCE_TIME_FILTER_OPTIONS,
    availableFilters: context.availableFilters,
    selectedPeriod,
    handleSelectPeriod,
    strategyFactorsFromSharedStart: context.strategyFactorsFromSharedStart,
    strategyTimeframePerformance: context.strategyTimeframePerformance,
    actualTimeframePerformance: context.actualTimeframePerformance,
    actualSummaryFilters: context.actualSummaryFilters,
  };
}

export const alphaPerformanceClockTestUtils = {
  getNextClockBoundaryMs,
};

export default useAlphaStrategyVsActualComparison;
